import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Button, Card, Col, DatePicker, Divider, Form, Input, InputNumber, Radio, Row, Select, Space, Steps, Switch, Typography,
} from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { campaignsApi } from '../services/catalogServiceAPI/campaignsProxy';
import { BlocksEditor } from './BlocksEditor';
import { ButtonsEditor } from './ButtonsEditor';
import { CampaignPreview } from './CampaignPreview';
import { SaleCard } from './SaleCard';
import {
    ACCOUNT_OPTIONS, DATE_FORMAT, EMAIL_VERIFIED_OPTIONS, USES_APP_OPTIONS, ENGAGEMENT_METRICS, FORMAT_OPTIONS, PLACEMENTS, PLAN_OPTIONS, PLATFORM_OPTIONS, PROVIDER_OPTIONS,
    STATUS_OPTIONS, SUBSCRIPTION_STATE_OPTIONS, THEME_OPTIONS, defaultContent, defaultFrequency, emptyAudience, emptySale,
} from './constants';

const { Text } = Typography;

const DEFAULT_ACCENT = '#ff9800';

const normalizeCodes = (values, transform) => Array.from(new Set(
    values.map((v) => transform(String(v).trim())).filter((v) => /^[A-Za-z]{2}$/.test(v)),
));

/** Free-typed two-letter codes (countries, languages), case-normalized; anything else typed is dropped. */
function CodesSelect({ value, onChange, transform, placeholder }) {
    return (
        <Select
            mode="tags"
            allowClear
            tokenSeparators={[',', ' ']}
            notFoundContent={null}
            suffixIcon={null}
            placeholder={placeholder}
            value={value}
            onChange={(values) => onChange(normalizeCodes(values, transform))}
            style={{ width: 260 }}
        />
    );
}

/** The server stores absent rules as "any"; the form keeps empty lists and blank ranges so the inputs stay controlled. */
function toFormState(campaign) {
    const audience = { ...emptyAudience(), ...(campaign?.audience ?? {}) };
    Object.keys(audience).forEach((key) => { if (audience[key] === null) audience[key] = emptyAudience()[key]; });
    audience.engagement = audience.engagement ?? {};
    audience.rolloutPercent = audience.rolloutPercent ?? 100;
    return {
        name: campaign?.name ?? '',
        status: campaign?.status ?? 'draft',
        priority: campaign?.priority ?? 0,
        inbox: campaign?.inbox ?? false,
        startAt: campaign?.startAt ? dayjs(campaign.startAt) : null,
        endAt: campaign?.endAt ? dayjs(campaign.endAt) : null,
        placements: campaign?.placements ?? [],
        placementParams: campaign?.placementParams ?? {},
        audience,
        frequency: { ...defaultFrequency(), ...(campaign?.frequency ?? {}) },
        content: campaign?.content ?? defaultContent(),
        sale: toSaleState(campaign?.sale),
    };
}

/** The stored sale has nulls where the form keeps blanks, and the per-store objects may be absent. */
function toSaleState(sale) {
    const empty = emptySale();
    if (!sale?.kind) return empty;
    return {
        kind: sale.kind,
        percentOff: sale.percentOff ?? null,
        salePriceLabel: sale.salePriceLabel ?? '',
        trialDays: sale.trialDays ?? null,
        stripe: { couponId: sale.stripe?.couponId ?? '', priceId: sale.stripe?.priceId ?? '' },
        apple: { offerIdentifier: sale.apple?.offerIdentifier ?? '', productIds: sale.apple?.productIds ?? {} },
        google: { offerId: sale.google?.offerId ?? '', productIds: sale.google?.productIds ?? {} },
    };
}

const listOrNull = (list) => (Array.isArray(list) && list.length > 0 ? list : null);
const rangeOrNull = (range) => (range && (range.min != null || range.max != null) ? { min: range.min ?? null, max: range.max ?? null } : null);
const idOrNull = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);
const productIdsOrNull = (map) => {
    const entries = Object.entries(map ?? {}).filter(([, v]) => idOrNull(v)).map(([k, v]) => [k, v.trim()]);
    return entries.length > 0 ? Object.fromEntries(entries) : null;
};

function toSaleRequest(sale) {
    if (!sale?.kind) return null;
    return {
        kind: sale.kind,
        percentOff: sale.percentOff ?? null,
        salePriceLabel: idOrNull(sale.salePriceLabel),
        trialDays: sale.trialDays ?? null,
        stripe: { couponId: idOrNull(sale.stripe?.couponId), priceId: idOrNull(sale.stripe?.priceId) },
        apple: { offerIdentifier: idOrNull(sale.apple?.offerIdentifier), productIds: productIdsOrNull(sale.apple?.productIds) },
        google: { offerId: idOrNull(sale.google?.offerId), productIds: productIdsOrNull(sale.google?.productIds) },
    };
}

function toRequest(state) {
    const a = state.audience;
    const engagement = Object.fromEntries(
        ENGAGEMENT_METRICS.map((m) => [m.key, rangeOrNull(a.engagement?.[m.key])]).filter(([, range]) => range),
    );
    return {
        name: state.name.trim(),
        status: state.status,
        priority: state.priority ?? 0,
        startAt: state.startAt ? state.startAt.toISOString() : null,
        endAt: state.endAt ? state.endAt.toISOString() : null,
        placements: state.placements,
        placementParams: Object.fromEntries(state.placements
            .filter((id) => PLACEMENTS.find((p) => p.id === id)?.param && state.placementParams[id] != null)
            .map((id) => [id, state.placementParams[id]])),
        audience: {
            platforms: listOrNull(a.platforms),
            minAppVersion: a.minAppVersion?.trim() || null,
            maxAppVersion: a.maxAppVersion?.trim() || null,
            accounts: listOrNull(a.accounts),
            plans: listOrNull(a.plans),
            subscriptionStates: listOrNull(a.subscriptionStates),
            providers: listOrNull(a.providers),
            countries: listOrNull(a.countries),
            languages: listOrNull(a.languages),
            engagement: Object.keys(engagement).length > 0 ? engagement : null,
            emailVerified: typeof a.emailVerified === 'boolean' ? a.emailVerified : null,
            usesApp: typeof a.usesApp === 'boolean' ? a.usesApp : null,
            seenCampaignIds: listOrNull(a.seenCampaignIds),
            notSeenCampaignIds: listOrNull(a.notSeenCampaignIds),
            rolloutPercent: a.rolloutPercent == null || a.rolloutPercent >= 100 ? null : a.rolloutPercent,
        },
        frequency: {
            maxShows: state.frequency.maxShows ?? null,
            minHoursBetween: state.frequency.minHoursBetween ?? null,
            maxDismissals: state.frequency.maxDismissals ?? null,
            stopAfterCta: state.frequency.stopAfterCta !== false,
        },
        content: {
            ...state.content,
            accent: state.content.accent || null,
            confetti: state.content.confetti === true,
            dismissible: state.content.dismissible !== false,
        },
        inbox: state.inbox,
        sale: toSaleRequest(state.sale),
    };
}

/** Step one: what the campaign is, when and to whom it shows. The server checks the sale's store ids in full. */
function validateMetadata(state) {
    if (!state.name.trim()) return 'Name is required';
    if (state.placements.length === 0) return 'Pick at least one placement';
    if (state.startAt && state.endAt && !state.endAt.isAfter(state.startAt)) return 'End must be after start';
    const { sale } = state;
    if (sale.kind === 'trial' && !sale.trialDays) return 'A trial sale needs the number of free days';
    if ((sale.kind === 'premium' || sale.kind === 'packs') && !sale.percentOff && !idOrNull(sale.salePriceLabel)) return 'A sale needs a percent off or a sale price to show';
    if (sale.kind && !state.endAt) return 'A campaign with a sale needs an end date, so the wallet can say when it ends';
    return null;
}

/** Step two: what it shows. */
function validateContent(state) {
    if (state.content.blocks.length === 0) return 'Add at least one content block';
    if (state.content.buttons.length === 0) return 'Add at least one button';
    if (state.content.buttons.some((b) => !b.label.trim())) return 'Every button needs a label';
    if (!state.sale.kind && state.content.blocks.some((b) => b.type === 'sale')) return 'A sale block needs a sale on step one';
    return null;
}

const STEPS = [
    { title: 'Campaign', description: 'Name, schedule, placements, audience' },
    { title: 'Content', description: 'Blocks and buttons, next to the live preview' },
];

function EngagementRow({ metric, range, onChange }) {
    return (
        <Row gutter={8} align="middle" style={{ marginBottom: 8 }}>
            <Col span={10}><Text>{metric.label}</Text></Col>
            <Col span={7}>
                <InputNumber min={0} placeholder="min" value={range?.min ?? null} onChange={(min) => onChange({ ...range, min })} style={{ width: '100%' }} />
            </Col>
            <Col span={7}>
                <InputNumber min={0} placeholder="max" value={range?.max ?? null} onChange={(max) => onChange({ ...range, max })} style={{ width: '100%' }} />
            </Col>
        </Row>
    );
}

/**
 * Editor for one campaign. `campaign` is the DTO to edit, or a seed for a
 * new one (a duplicate); `onSaved` gets the saved DTO.
 */
export function CampaignForm({ campaign, campaignId, onSaved, submitLabel = 'Save', headerExtra }) {
    const [state, setState] = useState(() => toFormState(campaign));
    const [others, setOthers] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [step, setStep] = useState(0);

    useEffect(() => {
        campaignsApi.list()
            .then((rows) => setOthers(rows.filter((c) => c.id !== campaignId)))
            .catch(() => setOthers([]));
    }, [campaignId]);

    const set = (patch) => setState((s) => ({ ...s, ...patch }));
    const setAudience = (patch) => setState((s) => ({ ...s, audience: { ...s.audience, ...patch } }));
    const setEngagement = (key, range) => setState((s) => ({ ...s, audience: { ...s.audience, engagement: { ...s.audience.engagement, [key]: range } } }));
    const setFrequency = (patch) => setState((s) => ({ ...s, frequency: { ...s.frequency, ...patch } }));
    const setContent = (patch) => setState((s) => ({ ...s, content: { ...s.content, ...patch } }));

    const otherOptions = useMemo(() => others.map((c) => ({ value: c.id, label: c.name })), [others]);
    const placementOptions = useMemo(() => PLACEMENTS.map((p) => ({ value: p.id, label: p.label, title: p.description })), []);
    const selectedPlacements = PLACEMENTS.filter((p) => state.placements.includes(p.id));

    const goTo = (next) => {
        if (next === step) return;
        if (next > 0) {
            const problem = validateMetadata(state);
            if (problem) { setError(problem); return; }
        }
        setError(null);
        setStep(next);
        window.scrollTo({ top: 0 });
    };

    const save = async () => {
        const problem = validateMetadata(state) ?? validateContent(state);
        if (problem) { setError(problem); return; }
        setSaving(true);
        setError(null);
        try {
            const body = toRequest(state);
            const saved = campaignId ? await campaignsApi.update(campaignId, body) : await campaignsApi.create(body);
            onSaved(saved);
        } catch (e) {
            setError(e.message);
            setSaving(false);
        }
    };

    return (
        <Form layout="vertical">
            <Steps
                current={step}
                onChange={goTo}
                items={STEPS}
                size="small"
                style={{ maxWidth: 720, marginBottom: 24 }}
            />

            {step === 0 && (
                <div style={{ maxWidth: 860 }}>
                    <Card title="Basics" size="small" style={{ marginBottom: 16 }} extra={headerExtra}>
                        <Form.Item label="Name" required help="Internal; users never see it.">
                            <Input value={state.name} maxLength={120} onChange={(e) => set({ name: e.target.value })} placeholder="Spring sale 2026" />
                        </Form.Item>
                        <Space size={16} wrap align="start">
                            <Form.Item label="Status">
                                <Select value={state.status} options={STATUS_OPTIONS} onChange={(status) => set({ status })} style={{ width: 160 }} />
                            </Form.Item>
                            <Form.Item label="Priority" help="Higher wins when several campaigns fit a placement.">
                                <InputNumber value={state.priority} onChange={(priority) => set({ priority: priority ?? 0 })} style={{ width: 120 }} />
                            </Form.Item>
                            <Form.Item label="Inbox" help="Also drop it in the inbox the first time a signed-in user sees it.">
                                <Switch checked={state.inbox} onChange={(inbox) => set({ inbox })} />
                            </Form.Item>
                        </Space>
                    </Card>

                    <Card title="Schedule" size="small" style={{ marginBottom: 16 }}>
                        <Space size={16} wrap align="start">
                            <Form.Item label="Start (your local time)" help="Empty = as soon as it is live.">
                                <DatePicker showTime format={DATE_FORMAT} value={state.startAt} onChange={(startAt) => set({ startAt })} />
                            </Form.Item>
                            <Form.Item label="End (your local time)" help="Empty = until paused or archived.">
                                <DatePicker showTime format={DATE_FORMAT} value={state.endAt} onChange={(endAt) => set({ endAt })} />
                            </Form.Item>
                        </Space>
                    </Card>

                    <SaleCard sale={state.sale} onChange={(sale) => set({ sale })} endAt={state.endAt} />

                    <Card title="Placements" size="small" style={{ marginBottom: 16 }}>
                        <Form.Item
                            label="Show at"
                            required
                            help={selectedPlacements.length > 0
                            ? selectedPlacements.map((p) => `${p.label}: ${p.description}`).join(' ')
                            : 'The moments a client may show this campaign. Hover an option for what it means.'}
                            >
                            <Select
                                mode="multiple"
                                allowClear
                                value={state.placements}
                                options={placementOptions}
                                optionFilterProp="label"
                                onChange={(placements) => set({ placements })}
                                />
                        </Form.Item>
                        {selectedPlacements.filter((p) => p.param).map((p) => (
                            <Form.Item key={p.id} label={`${p.label}: ${p.param.label}`} help={p.param.description}>
                                {p.param.options ? (
                                    <Select
                                        value={state.placementParams[p.id] ?? p.param.default}
                                        options={p.param.options}
                                        onChange={(v) => set({ placementParams: { ...state.placementParams, [p.id]: v } })}
                                        style={{ width: 240 }}
                                        />
                                ) : (
                                    <InputNumber
                                        min={p.param.min}
                                        max={p.param.max}
                                        precision={0}
                                        value={state.placementParams[p.id] ?? p.param.default}
                                        onChange={(n) => set({ placementParams: { ...state.placementParams, [p.id]: n } })}
                                        style={{ width: 120 }}
                                        />
                                )}
                            </Form.Item>
                        ))}
                    </Card>

                    <Card title="Audience" size="small" style={{ marginBottom: 16 }}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>Every rule left empty means &ldquo;any&rdquo;. A user is eligible when every set rule matches.</Text>
                        <Form.Item label="Platforms">
                            <Select mode="multiple" allowClear placeholder="Any platform" value={state.audience.platforms} options={PLATFORM_OPTIONS} onChange={(platforms) => setAudience({ platforms })} />
                        </Form.Item>
                        <Space size={16} wrap align="start">
                            <Form.Item label="Min app version" help="Apps only, like 1.2.0.">
                                <Input value={state.audience.minAppVersion ?? ''} onChange={(e) => setAudience({ minAppVersion: e.target.value })} placeholder="1.2.0" style={{ width: 140 }} />
                            </Form.Item>
                            <Form.Item label="Max app version" help="Apps only; an update nudge for old builds.">
                                <Input value={state.audience.maxAppVersion ?? ''} onChange={(e) => setAudience({ maxAppVersion: e.target.value })} placeholder="1.9.9" style={{ width: 140 }} />
                            </Form.Item>
                        </Space>
                        <Form.Item label="Accounts">
                            <Select mode="multiple" allowClear placeholder="Any account" value={state.audience.accounts} options={ACCOUNT_OPTIONS} onChange={(accounts) => setAudience({ accounts })} />
                        </Form.Item>
                        <Form.Item label="Plans" help="Narrows paid users. Only Best of both is sold today.">
                            <Select mode="multiple" allowClear placeholder="Any plan" value={state.audience.plans} options={PLAN_OPTIONS} onChange={(plans) => setAudience({ plans })} />
                        </Form.Item>
                        <Form.Item label="Subscription states">
                            <Select mode="multiple" allowClear placeholder="Any state" value={state.audience.subscriptionStates} options={SUBSCRIPTION_STATE_OPTIONS} onChange={(subscriptionStates) => setAudience({ subscriptionStates })} />
                        </Form.Item>
                        <Form.Item label="Billing providers">
                            <Select mode="multiple" allowClear placeholder="Any provider" value={state.audience.providers} options={PROVIDER_OPTIONS} onChange={(providers) => setAudience({ providers })} />
                        </Form.Item>
                        <Space size={16} wrap align="start" style={{ width: '100%' }}>
                            <Form.Item label="Countries" help="Two-letter codes (US, DE); type and press Enter.">
                                <CodesSelect
                                    placeholder="Any country"
                                    transform={(v) => v.toUpperCase()}
                                    value={state.audience.countries}
                                    onChange={(countries) => setAudience({ countries })}
                                    />
                            </Form.Item>
                            <Form.Item label="Languages" help="Two-letter codes (en, es).">
                                <CodesSelect
                                    placeholder="Any language"
                                    transform={(v) => v.toLowerCase()}
                                    value={state.audience.languages}
                                    onChange={(languages) => setAudience({ languages })}
                                    />
                            </Form.Item>
                        </Space>
                        <Divider style={{ margin: '8px 0 16px' }} />
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Engagement</Text>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>Inclusive bounds; leave a side empty to keep it open. Signed-in users only.</Text>
                        <Row gutter={8} style={{ marginBottom: 4 }}>
                            <Col span={10} />
                            <Col span={7}><Text type="secondary">Min</Text></Col>
                            <Col span={7}><Text type="secondary">Max</Text></Col>
                        </Row>
                        {ENGAGEMENT_METRICS.map((metric) => (
                            <EngagementRow
                                key={metric.key}
                                metric={metric}
                                range={state.audience.engagement?.[metric.key]}
                                onChange={(range) => setEngagement(metric.key, range)}
                                />
                        ))}
                        <Form.Item label="Email" help="Signed-in users with an email address only; Google and Apple sign-ins are verified.">
                            <Select
                                value={state.audience.emailVerified === true ? 'verified' : state.audience.emailVerified === false ? 'unverified' : 'any'}
                                options={EMAIL_VERIFIED_OPTIONS}
                                onChange={(value) => setAudience({ emailVerified: value === 'any' ? null : value === 'verified' })}
                                style={{ width: 200 }}
                                />
                        </Form.Item>
                        <Form.Item label="Mobile app" help="Recorded when a signed-in user opens the iOS or Android app. Guests count as never. Use 'Never' for get-the-app campaigns.">
                            <Select
                                value={state.audience.usesApp === true ? 'yes' : state.audience.usesApp === false ? 'no' : 'any'}
                                options={USES_APP_OPTIONS}
                                onChange={(value) => setAudience({ usesApp: value === 'any' ? null : value === 'yes' })}
                                style={{ width: 260 }}
                                />
                        </Form.Item>
                        <Divider style={{ margin: '8px 0 16px' }} />
                        <Form.Item label="Only users who have seen" help="All of these campaigns.">
                            <Select mode="multiple" allowClear placeholder="None" value={state.audience.seenCampaignIds} options={otherOptions} optionFilterProp="label" onChange={(seenCampaignIds) => setAudience({ seenCampaignIds })} />
                        </Form.Item>
                        <Form.Item label="Only users who have not seen" help="None of these campaigns.">
                            <Select mode="multiple" allowClear placeholder="None" value={state.audience.notSeenCampaignIds} options={otherOptions} optionFilterProp="label" onChange={(notSeenCampaignIds) => setAudience({ notSeenCampaignIds })} />
                        </Form.Item>
                        <Form.Item label="Rollout %" help="A stable hash of the user (or device) picks the bucket, so a user stays in or out.">
                            <InputNumber min={0} max={100} value={state.audience.rolloutPercent ?? 100} onChange={(rolloutPercent) => setAudience({ rolloutPercent: rolloutPercent ?? 100 })} style={{ width: 120 }} />
                        </Form.Item>
                    </Card>

                    <Card title="Frequency" size="small" style={{ marginBottom: 16 }}>
                        <Space size={16} wrap align="start">
                            <Form.Item label="Max shows" help="Lifetime, per user (per device for guests).">
                                <InputNumber min={1} value={state.frequency.maxShows} onChange={(maxShows) => setFrequency({ maxShows })} style={{ width: 120 }} />
                            </Form.Item>
                            <Form.Item label="Hours between shows">
                                <InputNumber min={0} value={state.frequency.minHoursBetween} onChange={(minHoursBetween) => setFrequency({ minHoursBetween })} style={{ width: 120 }} />
                            </Form.Item>
                            <Form.Item label="Max dismissals" help="Stop after this many, even if shows remain.">
                                <InputNumber min={1} value={state.frequency.maxDismissals} onChange={(maxDismissals) => setFrequency({ maxDismissals })} placeholder="∞" style={{ width: 120 }} />
                            </Form.Item>
                            <Form.Item label="Stop after CTA" help="Once the user pressed any non-dismiss button.">
                                <Switch checked={state.frequency.stopAfterCta !== false} onChange={(stopAfterCta) => setFrequency({ stopAfterCta })} />
                            </Form.Item>
                        </Space>
                    </Card>

                    {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
                    <Button type="primary" size="large" onClick={() => goTo(1)}>
                        Next: content <ArrowRightOutlined />
                    </Button>
                </div>
            )}

            {step === 1 && (
                <Row gutter={24} align="top">
                    <Col xs={24} xl={13}>
                        <Card title="Look" size="small" style={{ marginBottom: 16 }}>
                            <Space size={24} wrap align="start">
                                <Form.Item label="Format">
                                    <Radio.Group
                                        optionType="button"
                                        options={FORMAT_OPTIONS}
                                        value={state.content.format}
                                        onChange={(e) => setContent({ format: e.target.value })}
                                        />
                                </Form.Item>
                                <Form.Item label="Theme">
                                    <Radio.Group
                                        optionType="button"
                                        options={THEME_OPTIONS}
                                        value={state.content.theme}
                                        onChange={(e) => setContent({ theme: e.target.value })}
                                        />
                                </Form.Item>
                                <Form.Item label="Accent" help="Eyebrow, countdown and primary button on the hero theme.">
                                    <Space>
                                        <Input
                                            type="color"
                                            value={state.content.accent || DEFAULT_ACCENT}
                                            onChange={(e) => setContent({ accent: e.target.value })}
                                            style={{ width: 56, padding: 2 }}
                                            />
                                        <Text type="secondary">{state.content.accent || 'default'}</Text>
                                        {state.content.accent && <Button size="small" type="link" onClick={() => setContent({ accent: null })}>Clear</Button>}
                                    </Space>
                                </Form.Item>
                                <Form.Item label="Confetti">
                                    <Switch checked={state.content.confetti === true} onChange={(confetti) => setContent({ confetti })} />
                                </Form.Item>
                                <Form.Item label="Closable" help="Off: no X, and tapping outside does nothing; the buttons are the only way on (a gate such as the designing-time dialog).">
                                    <Switch checked={state.content.dismissible !== false} onChange={(dismissible) => setContent({ dismissible })} />
                                </Form.Item>
                            </Space>
                        </Card>
                        <Card title="Blocks" size="small" style={{ marginBottom: 16 }}>
                            <BlocksEditor blocks={state.content.blocks} onChange={(blocks) => setContent({ blocks })} />
                        </Card>
                        <Card title="Buttons" size="small" style={{ marginBottom: 16 }}>
                            <ButtonsEditor buttons={state.content.buttons} onChange={(buttons) => setContent({ buttons })} />
                        </Card>
                        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
                        <Space>
                            <Button size="large" icon={<ArrowLeftOutlined />} onClick={() => goTo(0)}>Back</Button>
                            <Button type="primary" size="large" loading={saving} onClick={save}>{submitLabel}</Button>
                        </Space>
                    </Col>
                    <Col xs={24} xl={11} style={{ position: 'sticky', top: 80 }}>
                        <CampaignPreview content={state.content} />
                    </Col>
                </Row>
            )}
        </Form>
    );
}
